import {
  Button,
  Icon,
  InputGroup,
  Keys,
  NumericInput,
} from '@blueprintjs/core';
import ABRCodeSelect from 'Components/Generic/BOSFilterSelect/ABRCodeSelect';
import AccountSelect from 'Components/Generic/BOSFilterSelect/AccountSelect';
import FiscalYearSelect from 'Components/Generic/BOSFilterSelect/FiscalYearSelect';
import {
  showSaveSuccessToast,
  showToast,
} from 'Components/Generic/BOSToasterUtil';
import AddNewButton from 'Components/Generic/Buttons/AddNewButton';
import ButtonFooter from 'Components/Generic/Buttons/ButtonFooter';
import DeleteButton from 'Components/Generic/Buttons/DeleteButton';
import SaveButton from 'Components/Generic/Buttons/SaveButton';
import UndoButton from 'Components/Generic/Buttons/UndoButton';
import NoContentBox from 'Components/Generic/Misc/NoContentBox';
import ABRCodeData from 'Data/ABRCodeData';
import BudgetData from 'Data/BudgetData';
import { useAlertContext } from 'Data/context/Alerts';
import DataManager from 'Lib/DataManager';
import { useStateWithRef } from 'Lib/hooks/useStateWithRef';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { areEquivalent, formatMoney, roundToHundredth } from 'Util';
import { CURRENT_FISCAL_YEAR } from 'Util/dateUtil';
import styles from './OrgBudgetSetup.module.css';

let DM = new DataManager();

const OrgBudgetSetup = ({
  org,
  selectedAccount,
  editable = false,
  compact = false,
  fieldsForNewBudget = {},
  onUpdateBudgets = () => {},
}) => {
  const { openDeleteDialog, showDeletedToast } = useAlertContext();

  const [fiscalYear, setFiscalYear, fiscalYearRef] =
    useStateWithRef(CURRENT_FISCAL_YEAR);
  const [budgetList, setBudgetList] = useState([]);
  const [hasBeenUpdated, setHasBeenUpdated] = useState(false);
  const orgRef = useRef();
  const newBudgetFieldValuesRef = useRef();
  const selectedAccountRef = useRef();
  const [yearToCopy, setYearToCopy] = useState();
  const yearToCopyComponentRef = useRef();

  useEffect(() => {
    DM.add(
      BudgetData.registerListener(
        'BUD_FOR_CORE_LIST_LOADED',
        onBudgetListLoaded,
      ),
      BudgetData,
    );
    DM.add(
      BudgetData.registerListener(
        'BUD_TO_COPY_LIST_LOADED',
        onBudgetListToCopyLoaded,
      ),
      BudgetData,
    );
    DM.add(
      BudgetData.registerListener('BLANK_RECORD_LOADED', onBlankBudgetLoaded),
      BudgetData,
    );
    DM.add(
      BudgetData.registerListener('RECORDS_UPDATED', onBudgetsUpdated),
      BudgetData,
    );
    DM.add(
      BudgetData.registerListener('RECORDS_DELETED', onBudgetDeleted),
      BudgetData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    orgRef.current = org;
    fetchBudgets();
  }, [org.id]);

  useEffect(() => {
    selectedAccountRef.current = selectedAccount;
    fetchBudgets();
  }, [selectedAccount]);

  useEffect(() => {
    fetchBudgets();
  }, [fiscalYear]);

  useEffect(() => {
    newBudgetFieldValuesRef.current = fieldsForNewBudget;
  }, [fieldsForNewBudget]);

  const fetchBudgets = () => {
    if (orgRef?.current.id && fiscalYearRef.current) {
      let whereClause = {
        '*parent_organization_id': orgRef.current.id,
        '*fiscal_year': fiscalYearRef.current,
      };
      if (selectedAccountRef?.current?.id) {
        whereClause['*account_id'] = selectedAccountRef.current.id;
      }
      BudgetData.getRecordList(
        {
          where: whereClause,
          _sort: 'charge_accounts.code,abr_code_fk',
          _pageSize: 200,
        },
        true,
        'BUD_FOR_CORE_LIST_LOADED',
      );
    } else {
      setBudgetList([]);
    }
  };

  const onBudgetListLoaded = (payload) => {
    if (payload) {
      setBudgetList(payload.records);
      setHasBeenUpdated(false);
    }
  };

  const copyBudgetFromYear = () => {
    if (!yearToCopy) {
      yearToCopyComponentRef.current.focus();
    }
    if (org.id && yearToCopy) {
      if (yearToCopy === 'Template') {
        populateTemplate();
      } else {
        let whereClause = {
          '*parent_organization_id': org.id,
          '*fiscal_year': yearToCopy,
        };
        if (selectedAccountRef?.current?.id) {
          whereClause['*account_id'] = selectedAccountRef.current.id;
        }
        BudgetData.getRecordList(
          {
            where: whereClause,
            _sort: 'charge_accounts.code,abr_code_fk',
          },
          true,
          'BUD_TO_COPY_LIST_LOADED',
        );
      }
    }
  };

  const populateTemplate = () => {
    let abrCodes = ABRCodeData.getAllABRCodes();
    if (abrCodes?.length) {
      setHasBeenUpdated(true);
      let accountIds = [];
      if (selectedAccountRef?.current?.id) {
        accountIds = [selectedAccountRef?.current?.id];
      } else {
        accountIds = orgRef?.current.charge_accounts?.map((act) => act.id) || [
          null,
        ];
      }
      // for each account add copies budget
      let copiedBudgetItems = accountIds.reduce((budList, accountId) => {
        accountId &&
          budList.push(
            ...abrCodes.map((abr) => {
              return {
                abr_code_fk: abr.abr_code,
                fiscal_year: `${fiscalYear}`,
                description: abr.description,
                account_id: accountId,
                ...newBudgetFieldValuesRef.current,
              };
            }),
          );
        return budList;
      }, []);
      setBudgetList((prevBudgetList) => [
        ...prevBudgetList,
        ...copiedBudgetItems,
      ]);
    } else {
      showToast('No template to duplicate.', 'warning', 'flag');
    }
  };

  const onBudgetListToCopyLoaded = (payload) => {
    if (payload?.records?.length) {
      setHasBeenUpdated(true);
      let copiedBudgetItems = payload.records.map((bud) => {
        return {
          ...bud,
          id: null,
          fiscal_year: fiscalYearRef.current,
          ...newBudgetFieldValuesRef.current,
        };
      });
      setBudgetList((prevBudgetList) => [
        ...prevBudgetList,
        ...copiedBudgetItems,
      ]);
    } else {
      showToast('No budgets to duplicate.', 'warning', 'flag');
    }
  };

  const onBlankBudgetLoaded = (newBudget) => {
    setHasBeenUpdated(true);
    let updatedLineItem = {
      ...newBudget,
      fiscal_year: fiscalYearRef.current,
      ...(selectedAccountRef?.current?.id && {
        account_id: selectedAccountRef?.current?.id,
      }),
      ...newBudgetFieldValuesRef.current,
    };
    setBudgetList((prevBudgetList) => [...prevBudgetList, updatedLineItem]);
  };

  const onBudgetsUpdated = (payload) => {
    if (payload) {
      showSaveSuccessToast('Budgets Saved');
      fetchBudgets();
      onUpdateBudgets();
    }
  };

  const updateItemValue = (itemToUpdate, updateField, updatedValue) => {
    const updatedArray = budgetList.map((item) => {
      if (item === itemToUpdate) {
        let originalItem = { ...item };
        let updatedItem = { ...item, [updateField]: updatedValue };
        if (!areEquivalent(originalItem, updatedItem)) {
          setHasBeenUpdated(true);
        }
        return updatedItem;
      }
      return item;
    });
    setBudgetList(updatedArray);
  };

  const saveClicked = () => {
    BudgetData.updateOrCreateRecords(budgetList);
  };

  const deleteBudget = (budget) => {
    BudgetData.deleteRecords([budget.id]);
  };

  const deleteClicked = (budget) => {
    if (budget.id) {
      openDeleteDialog({
        objectType: 'Budget',
        onConfirmDelete: () => deleteBudget(budget),
      });
    } else {
      setBudgetList((prevBudgets) =>
        prevBudgets.filter((item) => item !== budget),
      );
    }
  };

  const onBudgetDeleted = () => {
    showDeletedToast('Budget');
    fetchBudgets();
  };

  const undoChanges = () => {
    fetchBudgets();
  };

  let selectedAccountText = selectedAccount?.code || '';
  selectedAccountText += selectedAccount ? ` ${selectedAccount.name}` : '';

  let fiscalYearSelection = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: compact ? 4 : 10,
        marginBottom: 12,
        paddingLeft: compact ? 0 : 40,
        paddingRight: compact ? 0 : 40,
      }}>
      <div
        className={styles.account_details}
        style={{
          gap: compact ? 4 : 10,
        }}>
        {selectedAccount && (
          <span style={{ fontWeight: 600 }}>
            {compact ? 'Act.' : 'Account:'}
          </span>
        )}
        {selectedAccount && <span>{selectedAccountText}</span>}
      </div>
      <div
        className={styles.fiscal_year_selection}
        style={{
          gap: compact ? 4 : 10,
        }}>
        <span>Fiscal Year:</span>
        <FiscalYearSelect
          defaultSelectedYear={`${fiscalYear}`}
          onSelectFiscalYear={(year) => setFiscalYear(year?.year || '')}
        />
      </div>
    </div>
  );

  let headers = (
    <tr>
      <td
        style={{
          width: compact ? '25%' : '20%',
          ...(compact && { minWidth: 90 }),
        }}
        className={styles.table_header_cell}>
        {compact ? 'ABR' : 'ABR Code'}
      </td>
      {!selectedAccount && (
        <td
          style={{ width: compact ? '25%' : '20%' }}
          className={styles.table_header_cell}>
          Account
        </td>
      )}
      <td style={{ width: '40%' }} className={styles.table_header_cell}>
        Description
      </td>
      <td style={{ width: '30%' }} className={styles.table_header_cell}>
        Amount
      </td>
      <td style={{ whiteSpace: 'nowrap' }} className={styles.table_header_cell}>
        {compact ? 'FY' : 'Fiscal Year'}
      </td>
      {editable && <td></td>}
    </tr>
  );

  const handleBlurAmount = (e, budget) => {
    handleConfirm(budget, e.target.value);
  };

  const handleKeyDown = (e, budget) => {
    if (e.keyCode === Keys.ENTER) {
      handleConfirm(budget, e.target.value);
    }
  };

  const handleConfirm = (budget, amount) => {
    let updatedAmount = roundToHundredth(amount);
    updateItemValue(budget, 'amount', updatedAmount);
  };

  let accounts =
    budgetList?.reduce((accountList, budget) => {
      if (!accountList.includes(budget.account_id)) {
        accountList.push(budget.account_id);
      }
      return accountList;
    }, []) || [];

  let budgetListContent =
    !budgetList ||
    (budgetList.length === 0 && (
      <tr>
        <td colSpan={5}>
          <NoContentBox label={'No Budgets'} />
        </td>
      </tr>
    )) ||
    budgetList.map((budget, index) => {
      let accountText = budget?.charge_account?.code || '';
      accountText +=
        !compact && budget?.charge_account
          ? ` ${budget.charge_account.name}`
          : '';

      let altRowColor =
        accounts.includes(budget.account_id) &&
        accounts.indexOf(budget.account_id) % 2;

      return (
        <tr
          key={index}
          className={
            selectedAccount
              ? styles.selected_row
              : altRowColor
              ? styles.alt_row
              : ''
          }>
          <td style={{ textAlign: 'center' }}>
            {editable && (
              <ABRCodeSelect
                defaultSelectedABR={budget.abr_code_fk}
                onSelectABRCode={(abr) => {
                  abr && updateItemValue(budget, 'abr_code_fk', abr.abr_code);
                }}
              />
            )}
            {!editable && budget.abr_code_fk}
          </td>

          {!selectedAccount && (
            <td style={{ textAlign: 'center' }}>
              {editable && (
                <AccountSelect
                  orgId={org.id}
                  defaultSelectedId={budget.account_id}
                  onSelectAccount={(act) => {
                    act && updateItemValue(budget, 'account_id', act.id);
                  }}
                />
              )}
              {!editable && accountText}
            </td>
          )}

          <td style={{ textAlign: 'center' }}>
            {editable && (
              <InputGroup
                type='text'
                style={{ textAlign: 'center' }}
                value={budget.description || ''}
                readOnly={!editable}
                onChange={(event) => {
                  updateItemValue(budget, 'description', event.target.value);
                }}
              />
            )}
            {!editable && budget.description}
          </td>

          <td className={styles.money_cell}>
            {editable && (
              <NumericInput
                asyncControl
                fill
                style={{ textAlign: 'right', width: '100%' }}
                leftIcon={'dollar'}
                buttonPosition={'none'}
                minorStepSize={0.01}
                allowNumericCharactersOnly
                selectAllOnFocus
                value={formatMoney(budget.amount || 0)}
                onBlur={(e) => handleBlurAmount(e, budget)}
                onKeyDown={(e) => handleKeyDown(e, budget)}
              />
            )}
            {!editable && (
              <div
                className={styles.money_cell}
                style={{ display: 'flex', alignItems: 'baseline' }}>
                <Icon
                  size={12}
                  icon={'dollar'}
                  style={{ marginRight: 'auto' }}
                />
                {formatMoney(budget.amount)}
              </div>
            )}
          </td>

          <td style={{ textAlign: 'center' }}>
            {editable && (
              <FiscalYearSelect
                defaultSelectedYear={budget.fiscal_year}
                onSelectFiscalYear={(year) =>
                  updateItemValue(budget, 'fiscal_year', year?.year || '')
                }
                autoMarginLeft={compact}
                fill={compact}
              />
            )}
            {!editable && budget.fiscal_year}
          </td>

          {editable && (
            <td>
              <DeleteButton
                miniVersion
                onDeleteItem={() => deleteClicked(budget)}
                tooltipText={'Delete Budget'}
              />
            </td>
          )}
        </tr>
      );
    });

  let grandTotal =
    budgetList &&
    budgetList.reduce((acc, budget) => {
      if (budget.amount) {
        return acc + +budget.amount;
      } else {
        return acc;
      }
    }, 0);

  let totalRow = budgetList && budgetList.length > 0 && (
    <tr>
      <td />
      {!selectedAccount && <td />}
      <td className={styles.total_label}>Total:</td>
      <td className={styles.table_total_cell}>
        <div style={{ display: 'flex', alignItems: 'baseline' }}>
          <Icon size={14} icon={'dollar'} style={{ marginRight: 'auto' }} />
          {formatMoney(grandTotal)}
        </div>
      </td>
      <td />
      {editable && <td></td>}
    </tr>
  );

  const newButton = editable && (
    <AddNewButton clickHandler={BudgetData.getBlank} buttonText='Add Budget' />
  );

  const undoButton = editable && hasBeenUpdated && (
    <UndoButton clickHandler={undoChanges} />
  );

  const saveButton = editable && hasBeenUpdated && (
    <SaveButton clickHandler={saveClicked} />
  );

  const copyFromPreviousButton = editable && (
    <div
      className={
        !yearToCopy ? styles.copy_from_box_disabled : styles.copy_from_box
      }>
      <Button
        className={styles.copy_from_button}
        onClick={copyBudgetFromYear}
        icon={'duplicate'}>
        {'Copy from: '}
      </Button>
      <FiscalYearSelect
        ref={yearToCopyComponentRef}
        defaultSelectedYear={`${yearToCopy}`}
        onSelectFiscalYear={(year) => setYearToCopy(year?.year || '')}
        numberOfYearsToShow={5}
        additionalListItems={['Template']}
        simple
      />
    </div>
  );

  const footer = (
    <ButtonFooter marginTop={0}>
      {undoButton}
      {newButton}
      {copyFromPreviousButton}
      {saveButton}
    </ButtonFooter>
  );

  return (
    <div>
      {fiscalYearSelection}
      <table
        className={
          compact ? styles.budget_list_table_compact : styles.budget_list_table
        }>
        <thead>{headers}</thead>
        <tbody>
          {budgetListContent}
          {totalRow}
        </tbody>
      </table>
      {footer}
    </div>
  );
};

OrgBudgetSetup.propTypes = {
  org: PropTypes.object,
  selectedAccount: PropTypes.object,
  editable: PropTypes.bool,
  compact: PropTypes.bool,
  fieldsForNewBudget: PropTypes.object,
  onUpdateBudgets: PropTypes.func,
};

export default OrgBudgetSetup;
